import { avatarIds, randomFreeAvatar, type AvatarTier } from '@retusch/shared';

/**
 * Avatars : le catalogue, ses trois familles, et la regle d'acces de chacune.
 *
 * Trois tailles existent sur le disque (64, 128, 512). On sert la plus petite
 * qui couvre la taille demandee : un avatar de 28 px dans un rail de scores n'a
 * aucune raison de telecharger 512 px, et la difference se paie sur mobile.
 *
 * Les familles ne sont pas decoratives, elles portent le droit :
 *  - `free`    : tire au hasard, jamais choisi. C'est ce qu'a un joueur sans
 *                compte, et cela suffit a le distinguer des autres.
 *  - `account` : les quatre avatars d'un compte. Un est attribue au hasard a la
 *                creation, les quatre sont ensuite interchangeables.
 *  - `paid`    : reserves a l'achat. Cliquables par tous, mais le clic mene a
 *                la page d'achat, jamais a une selection silencieuse.
 */
export interface AvatarChoice {
  readonly id: string;
  readonly tier: AvatarTier;
}

// Les identifiants viennent de `shared` : ils voyagent dans le protocole, le
// serveur en attribue aux bots, une seule definition les porte.

/** Tailles disponibles sur le disque, en pixels, de la plus petite a la plus grande. */
const SIZES = [64, 128, 512] as const;

function family(tier: AvatarTier): AvatarChoice[] {
  return avatarIds(tier).map((id) => ({ id, tier }));
}

export const FREE_AVATARS = family('free');
export const ACCOUNT_AVATARS = family('account');
export const PAID_AVATARS = family('paid');
export const AVATARS: readonly AvatarChoice[] = [
  ...FREE_AVATARS,
  ...ACCOUNT_AVATARS,
  ...PAID_AVATARS,
];

const BY_ID = new Map(AVATARS.map((avatar) => [avatar.id, avatar]));

export function avatarById(id: string | null | undefined): AvatarChoice | null {
  if (id === undefined || id === null) return null;
  return BY_ID.get(id) ?? null;
}

export function isPaidAvatar(id: string | null | undefined): boolean {
  return avatarById(id)?.tier === 'paid';
}

/**
 * URL du fichier, a la plus petite taille qui couvre l'affichage.
 *
 * `rendered` est la taille CSS en pixels. On double pour les ecrans a haute
 * densite, sans quoi un avatar de 64 px affiche a 64 px serait flou sur tout
 * telephone recent.
 */
export function avatarSrc(id: string, rendered: number): string {
  const wanted = rendered * 2;
  const size = SIZES.find((candidate) => candidate >= wanted) ?? SIZES[SIZES.length - 1];
  return `/avatars/${size}/${id}.webp`;
}

/** Un des quatre avatars de compte, attribue a la creation. */
export function randomAccountAvatar(random: () => number = Math.random): string {
  const pick =
    ACCOUNT_AVATARS[Math.floor(random() * ACCOUNT_AVATARS.length)] ?? ACCOUNT_AVATARS[0];
  return pick?.id ?? 'account_1';
}

export { randomFreeAvatar };
export type { AvatarTier };
